import { Trans, useLingui } from "@lingui/react/macro";
import { z } from "zod";
import {
  isPatientInformationFieldVisible,
  useEconomicUnits,
  usePatient,
  usePatientInformationFields,
  usePatientTypes,
  useTeams,
  type IInformationFieldsConfig,
} from "@/api/Patients";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { useParams } from "react-router";
import styles from "./InformationList.module.scss";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import {
  getPatientFullName,
  patientStatusSchema,
  patientTypeDictionary,
  type IExistingPatient,
} from "@medoma/models/patients";
import {
  ActionCardLink,
  ActionCardReadOnly,
} from "@/components/ActionCard/ActionCard";
import { beautifyPostalCode } from "@medoma/models/postalCode";
import { useEmployee } from "@/api/Employee";
import { employeeName } from "@medoma/models/shifts";
import { dateName } from "@/Utils/DateUtils";

export const InformationList = () => {
  const { t } = useLingui();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { data: patient, isPending, isError, error } = usePatient(patientId);
  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  if (isPending || isPendingConditionalInformationFields) {
    return <Loading message={t`Laddar patientinformation`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patientinformation`} ${deducedError(error)}`}
      />
    );
  }

  if (isErrorConditionalInformationFields) {
    Sentry.captureException(errorConditionalInformationFields);
    return (
      <ErrorMessage message={deducedError(errorConditionalInformationFields)} />
    );
  }

  return (
    <LoadedInformationList
      patient={patient}
      conditionalInformationFields={conditionalInformationFields}
    />
  );
};

const LoadedInformationList = ({
  patient,
  conditionalInformationFields,
}: {
  patient: IExistingPatient;
  conditionalInformationFields: IInformationFieldsConfig;
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );
  const { data: cardiologyEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );
  const { data: patientTypes } = usePatientTypes();
  const { data: teams } = useTeams(patient.site.id);
  const { data: economicUnits } = useEconomicUnits(patient.site.id, {
    enabled: true,
  });

  const {
    name,
    address,
    phoneNumber,
    relatives,
    information,
    safetyAlarmText,
    pet,
    flags,
    careSummary,
    homeCare,
    lifeCare,
    healthcareJourneyNumber,
    diagnosis,
    externalCareEpisodeId,
    plannedAdmissionDateTime,
    plannedDischargeDateTime,
    primaryResponsible,
    referringUnit,
    placeOfAssessment,
    paymentExempt,
    language,
    reasonForAdmission,
    area,
    team,
    economicUnit,
    ward,
    patientType,
  } = patient;
  const patientName = getPatientFullName(name);
  const registeredAt =
    patient.status === patientStatusSchema.enum.prospect
      ? patient.registeredAt
      : null;
  const admittedAt =
    patient.status === patientStatusSchema.enum.admitted ||
    patient.status === patientStatusSchema.enum.discharged
      ? patient.admittedAt
      : null;
  const dischargedAt =
    patient.status === patientStatusSchema.enum.discharged
      ? patient.dischargedAt
      : null;

  const shouldShowField = (
    fieldName: keyof typeof conditionalInformationFields,
  ) =>
    isPatientInformationFieldVisible({
      fieldName,
      configValue: conditionalInformationFields[fieldName],
      patientStatus: patient.status,
    });

  return (
    <section className={styles.infoSection}>
      {/* REGISTRATION DATE */}
      {registeredAt ? (
        <ActionCardReadOnly title={t`Registreringsdatum`}>
          <Text element="p" color="faded">
            {dateName(registeredAt)}
          </Text>
        </ActionCardReadOnly>
      ) : null}

      {/* ADMISSION DATE */}
      {admittedAt ? (
        <ActionCardReadOnly title={t`Inskrivningsdatum`}>
          <Text element="p" color="faded">
            {dateName(admittedAt)}
          </Text>
        </ActionCardReadOnly>
      ) : null}

      {/* DISCHARGE DATE */}
      {dischargedAt ? (
        <ActionCardReadOnly title={t`Utskrivningsdatum`}>
          <Text element="p" color="faded">
            {dateName(dischargedAt)}
          </Text>
        </ActionCardReadOnly>
      ) : null}

      {/* PLANNED ADMISSION DATE TIME */}
      {shouldShowField("PlannedAdmissionDateTime") ? (
        <ActionCardLink
          title={t`Planerad inskrivning`}
          to="edit-planned-admission-date-time"
        >
          <Text element="p" color="faded">
            {plannedAdmissionDateTime
              ? dateName(plannedAdmissionDateTime)
              : "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* PLANNED DISCHARGE DATE TIME */}
      {shouldShowField("PlannedDischargeDateTime") ? (
        <ActionCardLink
          title={t`Planerad utskrivning`}
          to="edit-planned-discharge-date-time"
        >
          <Text element="p" color="faded">
            {plannedDischargeDateTime
              ? dateName(plannedDischargeDateTime)
              : "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* PATIENT TYPE */}
      {patientTypes && patientTypes.length > 1 ? (
        <ActionCardReadOnly title={t`Patienttyp`}>
          <Text element="p" color="faded">
            {t(patientTypeDictionary[patientType])}
          </Text>
        </ActionCardReadOnly>
      ) : null}

      {/* NAME */}
      <ActionCardLink title={t`Namn`} to="edit-name">
        <Text element="p" color="faded">
          {patientName}
        </Text>
      </ActionCardLink>

      {/* EXTERNAL CARE EPISODE ID */}
      {shouldShowField("ExternalCareEpisodeId") ? (
        <ActionCardLink
          title={t`Externt vårdperiodnummer`}
          to="edit-external-care-episode-id"
        >
          <Text element="p" color="faded">
            {externalCareEpisodeId ?? "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* DIAGNOSIS */}
      {shouldShowField("DiagnosisCode") ? (
        <ActionCardLink title={t`Diagnoskod`} to="edit-diagnosis-code">
          <Text element="p" color="faded">
            {diagnosis
              ? diagnosis.title
                ? // Happy path
                  `${diagnosis.code} - ${diagnosis.title}`
                : // Data created before diagnosis title was added, missing title
                  diagnosis.code
              : // No diagnosis
                "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* ADDRESS */}
      <ActionCardLink title={t`Adress`} to="edit-address">
        <Text element="p" color="faded">
          {address.addressLine1}
          <br />
          {beautifyPostalCode(address.postalCode, countryCode)} {address.city}
        </Text>
      </ActionCardLink>

      {/* ADDITIONAL ADDRESS INFORMATION */}
      {shouldShowField("AdditionalAdressInformation") ? (
        <ActionCardLink
          title={t`Övrig adressinformation`}
          to="edit-additional-address-information"
        >
          {address.additionalInformation ? (
            <TextWithLineBreaks color="faded">
              {address.additionalInformation}
            </TextWithLineBreaks>
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* KEY TO PATIENT */}
      {shouldShowField("KeyToPatient") ? (
        <ActionCardLink title={t`Nyckel`} to="edit-key-to-patient">
          {address.keyToPatient ? (
            <TextWithLineBreaks color="faded">
              {address.keyToPatient}
            </TextWithLineBreaks>
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* AREA */}
      {areaEnabled ? (
        <ActionCardLink title={t`Område`} to="edit-area">
          <Text element="p" color="faded">
            {area ? area.displayName : "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* TEAM */}
      {teams && teams.length > 0 ? (
        <ActionCardLink title={t`Team`} to="edit-team">
          <Text element="p" color="faded">
            {team ? team.displayName : "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* ECONOMIC UNIT */}
      {economicUnits && economicUnits.length > 1 ? (
        <ActionCardLink title={t`Ekonomisk enhet`} to="edit-economic-unit">
          <Text element="p" color="faded">
            {economicUnit ? economicUnit.displayName : "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* PHONE */}
      <ActionCardLink title={t`Telefonnummer`} to="edit-phone-number">
        <Text element="p" color="faded">
          {phoneNumber
            ? localizePhoneNumber({ countryCode, phoneNumber })
            : "-"}
        </Text>
      </ActionCardLink>

      {/* OTHER INFORMATION */}
      {shouldShowField("GeneralInformation") ? (
        <ActionCardLink
          title={t`Allmän information`}
          to="edit-other-information"
        >
          {information ? (
            <TextWithLineBreaks color="faded">{information}</TextWithLineBreaks>
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* CARE SUMMARY */}
      {shouldShowField("CareSummary") ? (
        <ActionCardLink title={t`Vårdsammanfattning`} to="edit-care-summary">
          {careSummary ? (
            <TextWithLineBreaks color="faded">{careSummary}</TextWithLineBreaks>
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* REASON FOR ADMISSION */}
      {shouldShowField("ReasonForAdmission") ? (
        <ActionCardLink
          title={t`Inskrivningsorsak`}
          to="edit-reason-for-admission"
        >
          {reasonForAdmission ? (
            <TextWithLineBreaks color="faded">
              {reasonForAdmission}
            </TextWithLineBreaks>
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* WARD */}
      {cardiologyEnabled ? (
        <ActionCardLink title={t`Avdelning`} to="edit-ward">
          <Text element="p" color="faded">
            {ward.displayName}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* RELATIVES */}
      {shouldShowField("CloseContact") ? (
        <ActionCardLink title={t`Anhöriga`} to="edit-relatives">
          {relatives.length > 0 ? (
            relatives.map(
              ({ id, name, relation, isLivingTogether, phoneNumber }) => {
                const optionalLivesTogetherWithSegment = isLivingTogether
                  ? t`bor med ${patientName} och`
                  : "";
                const localizedPhoneNumber = phoneNumber
                  ? localizePhoneNumber({
                      countryCode,
                      phoneNumber,
                    })
                  : null;

                return (
                  <Text element="p" color="faded" key={id}>
                    {localizedPhoneNumber ? (
                      <Trans>
                        {name} ({relation}) {optionalLivesTogetherWithSegment}{" "}
                        nås på {localizedPhoneNumber}
                      </Trans>
                    ) : (
                      <Trans>
                        {name} ({relation}) {optionalLivesTogetherWithSegment}{" "}
                        saknar telefonnummer
                      </Trans>
                    )}
                  </Text>
                );
              },
            )
          ) : (
            <Text element="p" color="faded">
              -
            </Text>
          )}
        </ActionCardLink>
      ) : null}

      {/* PRIMARY RESPONSIBLE */}
      {shouldShowField("PrimaryResponsible") ? (
        <PrimaryResponsibleDisplay primaryResponsible={primaryResponsible} />
      ) : null}

      {/* REFERRING UNIT */}
      {shouldShowField("ReferringUnit") ? (
        <ActionCardLink title={t`Remitterande enhet`} to="edit-referring-unit">
          <TextWithLineBreaks color="faded">
            {referringUnit ?? "-"}
          </TextWithLineBreaks>
        </ActionCardLink>
      ) : null}

      {/* PLACE OF ASSESSMENT */}
      {shouldShowField("PlaceOfAssessment") ? (
        <ActionCardLink
          title={t`Plats för bedömning`}
          to="edit-place-of-assessment"
        >
          <TextWithLineBreaks color="faded">
            {placeOfAssessment ?? "-"}
          </TextWithLineBreaks>
        </ActionCardLink>
      ) : null}

      {/* PAYMENT EXEMPT */}
      {shouldShowField("PaymentExempt") ? (
        <ActionCardLink title={t`Betalningsbefriad`} to="edit-payment-exempt">
          <Text element="p" color="faded">
            {paymentExempt ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* HEALTHCARE JOURNEY NUMBER */}
      {shouldShowField("MedicalTransportNumber") ? (
        <ActionCardLink
          title={t`Sjukresenummer`}
          to="edit-healthcare-journey-number"
        >
          <Text element="p" color="faded">
            {healthcareJourneyNumber ?? "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* SAFETY ALARM */}
      {shouldShowField("PersWristband") ? (
        <ActionCardLink title={t`Sociala insatser`} to="edit-safety-alarm">
          <TextWithLineBreaks color="faded">
            {safetyAlarmText ?? "-"}
          </TextWithLineBreaks>
        </ActionCardLink>
      ) : null}

      {/* PETS */}
      {shouldShowField("Pet") ? (
        <ActionCardLink title={t`Husdjur`} to="edit-pets">
          <Text element="p" color="faded">
            {pet ?? "-"}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* FLAGS */}
      {shouldShowField("Flags") ? (
        <ActionCardLink title={t`Flaggningar`} to="edit-flags">
          <TextWithLineBreaks color="faded">{flags ?? "-"}</TextWithLineBreaks>
        </ActionCardLink>
      ) : null}

      {/* LANGUAGE */}
      {shouldShowField("Language") ? (
        <ActionCardLink title={t`Språk`} to="edit-language">
          <TextWithLineBreaks color="faded">
            {language ?? "-"}
          </TextWithLineBreaks>
        </ActionCardLink>
      ) : null}

      {/* HOMECARE */}
      {shouldShowField("HomeCare") ? (
        <ActionCardLink title={t`Hemtjänst`} to="edit-homecare">
          <Text element="p" color="faded">
            {homeCare ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}
          </Text>
        </ActionCardLink>
      ) : null}

      {/* LIFECARE */}
      {shouldShowField("LifeCare") ? (
        <ActionCardLink title={t`LifeCare`} to="edit-lifecare">
          <Text element="p" color="faded">
            {lifeCare ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}
          </Text>
        </ActionCardLink>
      ) : null}
    </section>
  );
};

const PrimaryResponsibleDisplay = ({
  primaryResponsible,
}: {
  primaryResponsible: number | null | undefined;
}) => {
  const { t } = useLingui();
  if (primaryResponsible === null || primaryResponsible === undefined) {
    return (
      <ActionCardLink title={t`Patientansvarig`} to="edit-primary-responsible">
        <Text element="p" color="faded">
          -
        </Text>
      </ActionCardLink>
    );
  } else {
    return (
      <ExistingPrimaryResponsibleDisplay
        primaryResponsible={primaryResponsible}
      />
    );
  }
};

const ExistingPrimaryResponsibleDisplay = ({
  primaryResponsible,
}: {
  primaryResponsible: number;
}) => {
  const { t } = useLingui();
  const { data: employee, isPending } = useEmployee({
    employeeNumber: primaryResponsible,
  });

  return (
    <ActionCardLink title={t`Patientansvarig`} to="edit-primary-responsible">
      {isPending ? (
        <Loading message={t`Hämtar medarbetare`} />
      ) : employee ? (
        <Text element="p" color="faded">
          {employeeName(employee, { employeeNameFormat: "full" })}
        </Text>
      ) : (
        <Text element="p" color="faded">
          -
        </Text>
      )}
    </ActionCardLink>
  );
};
